// client/src/hooks/useChat.js

import { useState, useRef, useEffect, useCallback } from 'react';
import { scrollToBottom, generateId } from '../utils/chatHelpers';
import { savePDFToCache } from '../utils/pdf_cache_setup';
import { fetchWithAuth } from '../context/AuthContext';


const API = process.env.REACT_APP_WEB_SERVER_URL || 'http://localhost:8080';

export function useChat() {

  // ─── Loading states ───────────────────────────────────────────
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isLoadingReport, setIsLoadingReport] = useState(false);

  // ─── Core chat state ──────────────────────────────────────────
  const [chatStarted, setChatStarted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [turns, setTurns] = useState([]);
  const [feedbackState, setFeedbackState] = useState({});
  const [suggestionFeedbackState, setSuggestionFeedbackState] = useState({});
  const [reportFeedbackState, setReportFeedbackState] = useState({});


  // ─── Project state (replaces sessionId) ───────────────────────
  const [projectId, setProjectId] = useState(null);
  const [projectName, setProjectName] = useState('');

  // ─── Turn pagination ──────────────────────────────────────────
  const [hasMoreTurns, setHasMoreTurns] = useState(false);
  const [turnCursor, setTurnCursor] = useState(null); // oldest turnNumber loaded
  const [isLoadingOlderTurns, setIsLoadingOlderTurns] = useState(false);

  // ─── Right pane state ─────────────────────────────────────────
  const [activeSuggestionTurnId, setActiveSuggestionTurnId] = useState(null);
  const [suggestions, setSuggestions] = useState({});
  const [typingTurnId, setTypingTurnId] = useState(null);
  const [typingSuggestionId, setTypingSuggestionId] = useState(null);
  const [extractedSuggestions, setExtractedSuggestions] = useState({});
  const [selectedSuggestions, setSelectedSuggestions] = useState({});
  const [reports, setReports] = useState({});
  const [activeReportId, setActiveReportId] = useState(null);
  const [typingReportId, setTypingReportId] = useState(null);
  const [reportLoadingPhase, setReportLoadingPhase] = useState('init');

  // ─── File tracking ────────────────────────────────────────────
  const [validUserMessages, setValidUserMessages] = useState([]);
  const [allFiles, setAllFiles] = useState([]);

  // ─── Refs ─────────────────────────────────────────────────────
  const prevActiveTurnRef = useRef(null);
  const prevActiveReportRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const rightPaneRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const isRightNearBottomRef = useRef(true);
  const userScrolledAwayRef = useRef(false);
  const userScrolledAwayRightRef = useRef(false);

  // Maps client turnId → DB turnId (for feedback calls)
  const turnMappingsRef = useRef({});
  // Maps client turnId → DB suggestionOption IDs { title: dbId }
  const suggestionOptionMappingsRef = useRef({});

  // ─── Auto-scroll effects ──────────────────────────────────────
  useEffect(() => {
    if (typingTurnId !== null && !userScrolledAwayRef.current) scrollMessagesToBottom();
  }, [turns, typingTurnId]);

  useEffect(() => {
    if (typingSuggestionId !== null && !userScrolledAwayRightRef.current) scrollRightToBottom();
  }, [suggestions, typingSuggestionId]);

  useEffect(() => {
    if (typingReportId !== null && !userScrolledAwayRightRef.current) scrollRightToBottom();
  }, [reports, typingReportId]);

  // ─── Clear selectedSuggestions when pane changes ─────────────
  useEffect(() => {
    const currentTurnId = activeSuggestionTurnId;
    const currentReportId = activeReportId;
    // Determine if user navigated away from suggestions view
    const wasViewingSuggestions = prevActiveTurnRef.current !== null && prevActiveReportRef.current === null;
    const isNowViewingReport = currentTurnId !== null && currentReportId !== null;
    const isNowViewingDifferentTurn = prevActiveTurnRef.current !== null && prevActiveTurnRef.current !== currentTurnId;
    const isNowClosingPane = prevActiveTurnRef.current !== null && currentTurnId === null;
    // Clear selections when:
    // 1. Switching from suggestions to report (same turn)
    // 2. Switching to different turn's suggestions
    // 3. Closing the right pane
    if (wasViewingSuggestions && isNowViewingReport && prevActiveTurnRef.current === currentTurnId) {
      const turnId = prevActiveTurnRef.current;
      setSelectedSuggestions(prev => { const u = { ...prev }; delete u[turnId]; return u; }); //updated
    } else if (isNowViewingDifferentTurn) {
      const prevTurnId = prevActiveTurnRef.current;
      setSelectedSuggestions(prev => { const u = { ...prev }; delete u[prevTurnId]; return u; });
    } else if (isNowClosingPane) {
      const prevTurnId = prevActiveTurnRef.current;
      setSelectedSuggestions(prev => { const u = { ...prev }; delete u[prevTurnId]; return u; });
    }

    prevActiveTurnRef.current = currentTurnId;
    prevActiveReportRef.current = currentReportId;
  }, [activeSuggestionTurnId, activeReportId]);

  // ─── Scroll helpers ───────────────────────────────────────────
  const scrollMessagesToBottom = () => scrollToBottom(messagesContainerRef);
  const scrollRightToBottom = () => scrollToBottom(rightPaneRef);

  const onScrollMessages = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = distance < 80;
    if (typingTurnId !== null && distance > 80) userScrolledAwayRef.current = true;
  };

  const handleRightPaneScroll = () => {
    const el = rightPaneRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    isRightNearBottomRef.current = distance < 80;
    if (typingSuggestionId !== null && distance > 80) userScrolledAwayRightRef.current = true;
  };

  // ─── Reset (New Project) ──────────────────────────────────────
  const resetChat = useCallback(() => {
    setProjectId(null);
    setProjectName('');
    setChatStarted(false);
    setTurns([]);
    setFeedbackState({});
    setSuggestionFeedbackState({});
    setReportFeedbackState({});
    setHasMoreTurns(false);
    setTurnCursor(null);
    setActiveSuggestionTurnId(null);
    setSuggestions({});
    setTypingTurnId(null);
    setTypingSuggestionId(null);
    setExtractedSuggestions({});
    setSelectedSuggestions({});
    setReports({});
    setActiveReportId(null);
    setTypingReportId(null);
    setValidUserMessages([]);
    setAllFiles([]);
    turnMappingsRef.current = {};
    suggestionOptionMappingsRef.current = {};
    userScrolledAwayRef.current = false;
    userScrolledAwayRightRef.current = false;
  }, []);

  // ─── Load existing project (sidebar click) ────────────────────
  const loadProject = useCallback(async (id, name) => {
    resetChat();

    setProjectId(id);
    setProjectName(name);
    setChatStarted(true);

    try {
      const res = await fetchWithAuth(`${API}/api/projects/${id}/turns?limit=8`, {
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      // Restore turns into local state format
      const restoredTurns = data.turns.map((t) => ({
        id: t.id,           // DB turn id used as client id for loaded turns
        dbTurnId: t.id,
        userText: t.userMessage || '',
        botFollowup: t.assistantMessage || '',
        suggestion: t.suggestionSummary !== null ? t.suggestionSummary : undefined,
        // currentFiles: [],
        turnNumber: t.turnNumber,
        isLoaded: true,     // flag: this turn came from DB, not typed live
        currentFiles: (t.files || []).map(f => ({
          id: f.id,
          name: f.filename,
          type: f.mimeType,
          size: f.size,
          downloadURL: f.downloadUrl,   // signed URL from server
          storagePath: f.s3Key,
          uploading: false,
          uploadError: false,
        })),
      }));

      setTurns(restoredTurns);
      setHasMoreTurns(data.hasMore);
      setTurnCursor(data.nextCursor);

      const restoredFeedback = {};
      const restoredSuggestionFeedback = {};
      const restoredReportFeedback = {};

      // Restore suggestionOptions mappings
      data.turns.forEach((t) => {
        turnMappingsRef.current[t.id] = t.id;
        // Assistant feedback
        if (t.assistantFeedback?.length > 0) {
          const fb = t.assistantFeedback[0];
          restoredFeedback[t.id] = { type: fb.type, comment: fb.comment || '' };
        }

        if (t.suggestionOptions?.length > 0) {
          const optMap = {};
          t.suggestionOptions.forEach((opt) => { optMap[opt.title] = opt.id; });
          suggestionOptionMappingsRef.current[t.id] = optMap;

          // Restore extracted suggestions for right pane
          const extracted = {};
          t.suggestionOptions.forEach((opt) => { extracted[opt.title] = opt.description; });
          setExtractedSuggestions(prev => ({ ...prev, [t.id]: extracted }));
          setSuggestions(prev => ({ ...prev, [t.id]: t.suggestionSummary || '' }));
          // Suggestion feedback — stored on first option (Option A)
          const firstOption = t.suggestionOptions[0];
          if (firstOption.feedback?.length > 0) {
            const fb = firstOption.feedback[0];
            restoredSuggestionFeedback[t.id] = { type: fb.type, comment: fb.comment || '' };
          }
        }



        // Restore reports
        // useChat.js — loadProject() — update report restore block
        if (t.reports?.length > 0) {
          const restoredReports = t.reports.map((r) => {
            // Map selected UUIDs back to titles using suggestionOptions
            const selectedTitles = r.selectedOptions
              .map(s => t.suggestionOptions.find(opt => opt.id === s.suggestionOptionId)?.title)
              .filter(Boolean);

            // Report feedback — keyed by turnId_vVersion
            if (r.feedback?.length > 0) {
              const fb = r.feedback[0];
              restoredReportFeedback[`${t.id}_v${r.version}`] = { type: fb.type, comment: fb.comment || '' };
            }

            return {
              version: r.version,
              content: r.markdown,
              isComplete: true,
              selectedSuggestions: selectedTitles, // ← titles, not UUIDs
            };
          });
          setReports(prev => ({ ...prev, [t.id]: restoredReports }));
        }
      });

      setFeedbackState(restoredFeedback);
      setSuggestionFeedbackState(restoredSuggestionFeedback);
      setReportFeedbackState(restoredReportFeedback);

      // Restore validUserMessages for API calls
      const validMsgs = data.turns
        .filter((t) => t.userMessage)
        .map((t) => t.userMessage);
      setValidUserMessages(validMsgs);

    } catch (err) {
      console.error('Error loading project:', err);
    }
  }, [resetChat]);

  // ─── Load older turns (scroll to top pagination) ──────────────
  const loadOlderTurns = useCallback(async () => {
    if (!projectId || !hasMoreTurns || isLoadingOlderTurns) return;

    setIsLoadingOlderTurns(true);
    try {
      const res = await fetchWithAuth(
        `${API}/api/projects/${projectId}/turns?cursor=${turnCursor}`,
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      const olderTurns = data.turns.map((t) => ({
        id: t.id,
        dbTurnId: t.id,
        userText: t.userMessage || '',
        botFollowup: t.assistantMessage || '',
        suggestion: t.suggestionSummary !== null ? t.suggestionSummary : undefined,
        currentFiles: (t.files || []).map(f => ({
          id: f.id,
          name: f.filename,
          type: f.mimeType,
          size: f.size,
          downloadURL: f.downloadUrl,   // signed URL from server
          storagePath: f.s3Key,
          uploading: false,
          uploadError: false,
        })),
        turnNumber: t.turnNumber,
        isLoaded: true,
      }));

      // Preserve scroll position when prepending
      const container = messagesContainerRef.current;
      const prevScrollHeight = container?.scrollHeight || 0;

      setTurns(prev => [...olderTurns, ...prev]); // prepend older turns
      setHasMoreTurns(data.hasMore);
      setTurnCursor(data.nextCursor);

      // Restore scroll so user doesn't jump
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight - prevScrollHeight;
        }
      });

    } catch (err) {
      console.error('Error loading older turns:', err);
    } finally {
      setIsLoadingOlderTurns(false);
    }
  }, [projectId, hasMoreTurns, turnCursor, isLoadingOlderTurns]);

  // ─── Rename project ───────────────────────────────────────────
  const renameProject = useCallback(async (id, newName) => {
    try {
      const res = await fetchWithAuth(`${API}/api/projects/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
      });
      if (!res.ok) throw new Error('Rename failed');
      setProjectName(newName);
    } catch (err) {
      console.error('Error renaming project:', err);
    }
  }, []);

  // ─── File upload handler ──────────────────────────────────────
  const handleFileUpload = async (file, turnId, currentProjectId) => {
    if (!currentProjectId || !turnId) {
      console.warn('No projectId or turnId — skipping file upload');
      return null;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', currentProjectId);
      formData.append('turnId', turnId);
      formData.append('originalFilename', file.name);

      // ⚠️ Do NOT set Content-Type header — browser sets it automatically with boundary
      const res = await fetchWithAuth(`${API}/files/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('Upload failed:', err);
        return null;
      }

      const data = await res.json();

      if (data.status === 'success' && data.file) {
        console.log('✅ File uploaded:', data.file.filename);
        return data.file; // { id, filename, storedFilename, storagePath, downloadUrl, size, mimeType, uploadedAt }
      }

      return null;
    } catch (error) {
      console.error('File upload error:', error);
      return null;
    }
  };


  // ─── API helpers ──────────────────────────────────────────────
  const convertFilesToBase64 = async (files) => {
    return Promise.all(files.map(file => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve({ name: file.name, type: file.type, content: reader.result.split(',')[1] });
      reader.onerror = reject;
      reader.readAsDataURL(file);
    })));
  };

  const checkGuardrail = async (message, files) => {
    const res = await fetchWithAuth(`${API}/api/analysis/guardrail`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, files: files ? await convertFilesToBase64(files) : [] }),
    });
    if (!res.ok) throw new Error(`Guardrail error: ${res.status}`);
    return res.json();
  };

  const streamClarifications = async (userMessages, fileMsgs, onChunk) => {
    const res = await fetchWithAuth(`${API}/api/analysis/get_clarifications_stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_messages: userMessages, file_msgs: fileMsgs }),
    });
    if (!res.ok) throw new Error(`Clarifications error: ${res.status}`);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      onChunk(decoder.decode(value, { stream: true }));
      if (!userScrolledAwayRef.current) setTimeout(scrollMessagesToBottom, 0);
    }
  };

  const streamOptimizations = async (userMessages, fileMsgs, onChunk) => {
    const res = await fetchWithAuth(`${API}/api/analysis/get_optimizations_stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_messages: userMessages, file_msgs: fileMsgs }),
    });
    if (!res.ok) throw new Error(`Optimizations error: ${res.status}`);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      onChunk(decoder.decode(value, { stream: true }));
      if (!userScrolledAwayRightRef.current) setTimeout(scrollRightToBottom, 0);
    }
  };

  const extractOptimizations = async (assessmentText) => {
    const res = await fetchWithAuth(`${API}/api/analysis/extract_optimizations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assessment_text: assessmentText }),
    });
    if (!res.ok) throw new Error(`Extract error: ${res.status}`);
    return res.json();
  };

  const getMeasurements = async (userMessages, fileMsgs) => {
    const res = await fetchWithAuth(`${API}/api/analysis/get_measurements`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_messages: userMessages, file_msgs: fileMsgs }),
    });
    if (!res.ok) throw new Error(`Measurements error: ${res.status}`);
    return res.json();
  };

  const getMaterialCostList = async (userMessages, optimizationText, acceptedSuggestions, measurements) => {
    const res = await fetchWithAuth(`${API}/api/analysis/material_cost_list`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_messages: userMessages, assessment_text: optimizationText, accepted_suggestions: acceptedSuggestions, measurements }),
    });
    if (!res.ok) throw new Error(`Cost list error: ${res.status}`);
    return res.json();
  };

  const streamReport = async (userMessages, assessmentText, acceptedSuggestions, measurements, costDetails, onChunk) => {
    const res = await fetchWithAuth(`${API}/api/analysis/generate_report_stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_messages: userMessages, assessment_text: assessmentText, accepted_suggestions: acceptedSuggestions, measurements, cost_details: costDetails }),
    });
    if (!res.ok) throw new Error(`Report stream error: ${res.status}`);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      onChunk(decoder.decode(value, { stream: true }));
      if (!userScrolledAwayRightRef.current) setTimeout(scrollRightToBottom, 0);
    }
  };

  const streamText = async (text, onChunk, speed = 15) => {
    for (const ch of text) {
      onChunk(ch);
      if (!userScrolledAwayRef.current) setTimeout(scrollMessagesToBottom, 0);
      await new Promise(r => setTimeout(r, speed));
    }
  };

  // ─── Main send handler ────────────────────────────────────────
  const handleSend = async (text, files) => {
    setChatStarted(true);
    setIsSending(true);

    let clientTurnId = null;

    try {
      // ── Step A: Lazy project creation on first message ──
      let currentProjectId = projectId;
      if (!currentProjectId) {
        const autoName = text?.trim()?.slice(0, 50) || 'Untitled Project';
        try {
          const res = await fetchWithAuth(`${API}/api/projects`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: autoName }),
          });
          const newProject = await res.json();
          currentProjectId = newProject.id;
          setProjectId(currentProjectId);
          setProjectName(newProject.name);
        } catch (err) {
          console.error('Failed to create project:', err);
          // Continue without DB — don't block the chat
        }
      }



      // ── Step B: Optimistic UI update ──
      clientTurnId = generateId();
      const currentTurnNumber = turns.length + 1;
      // Create file metadata immediately from File objects
      // This gives you the display info (name, type, size) without waiting for upload
      let dbTurnId = null;
      if (currentProjectId) {
        try {
          const res = await fetchWithAuth(`${API}/api/projects/${currentProjectId}/turns`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ turnNumber: currentTurnNumber, userMessage: text || '' }),
          });
          const saved = await res.json();
          dbTurnId = saved.turnId;
          turnMappingsRef.current[clientTurnId] = dbTurnId; // map early
        } catch (err) {
          console.error('Failed to create turn:', err);
          // non-fatal — continue without DB
        }
      }
      // ── Step B-UI: Optimistic UI update ──
      const immediateFileData = files?.length > 0
        ? files.map(f => ({ name: f.name, type: f.type, size: f.size, uploading: true, downloadURL: null }))
        : [];

      setTurns(prev => [...prev, {
        id: clientTurnId,
        userText: text || '',
        botFollowup: '',
        suggestion: undefined,
        currentFiles: immediateFileData,// Start with empty array
        turnNumber: currentTurnNumber,
      }]);
      setTypingTurnId(clientTurnId);
      // Reset scroll tracking
      userScrolledAwayRef.current = false;
      userScrolledAwayRightRef.current = false;
      isNearBottomRef.current = true;
      setTimeout(scrollMessagesToBottom, 0);

      // ── Step B2: Upload files to S3 in background ──
      if (files?.length > 0) {
        for (let i = 0; i < files.length; i++) {
          try {
            const uploaded = await handleFileUpload(files[i], dbTurnId, currentProjectId);

            setTurns(prev => prev.map(t => {
              if (t.id !== clientTurnId) return t;
              const updatedFiles = [...t.currentFiles];
              updatedFiles[i] = uploaded
                ? { ...uploaded, uploading: false }
                : { ...updatedFiles[i], uploading: false, uploadError: true };
              return { ...t, currentFiles: updatedFiles };
            }));
          } catch {
            setTurns(prev => prev.map(t => {
              if (t.id !== clientTurnId) return t;
              const updatedFiles = [...t.currentFiles];
              updatedFiles[i] = { ...updatedFiles[i], uploading: false, uploadError: true };
              return { ...t, currentFiles: updatedFiles };
            }));
          }
        }
      }

      if (files?.length > 0) {
        setAllFiles(prev => [...prev, ...files]);
      }



      // ── Step C: Guardrail check ──
      const guardrailResponse = await checkGuardrail(text, files);
      const passedGuardrail = guardrailResponse.status === 'success' && guardrailResponse.message === '1';

      if (passedGuardrail) {
        const updatedValidMessages = text?.trim() ? [...validUserMessages, text] : validUserMessages;
        setValidUserMessages(updatedValidMessages);

        const allFilesForConversion = [...allFiles, ...(files || [])];
        const fileMsgs = allFilesForConversion.length > 0 ? await convertFilesToBase64(allFilesForConversion) : [];

        setTurns(prev => prev.map(t => t.id === clientTurnId ? { ...t, suggestion: '' } : t));
        setSuggestions(prev => ({ ...prev, [clientTurnId]: '' }));
        setActiveSuggestionTurnId(clientTurnId);

        let assistantMessageContent = '';
        let optimizationText = '';
        let extractedOptimizationsData = {};

        // ── Step D: Stream clarifications + optimizations in parallel ──
        const clarificationsPromise = (async () => {
          try {
            let streamed = '';
            await streamClarifications(updatedValidMessages, fileMsgs, (chunk) => {
              streamed += chunk;
              assistantMessageContent = streamed;
              setTurns(prev => prev.map(t => t.id === clientTurnId ? { ...t, botFollowup: streamed } : t));
            });
          } catch (err) {
            console.error('Clarifications error:', err);
            setTurns(prev => prev.map(t => t.id === clientTurnId ? { ...t, botFollowup: t.botFollowup + '\n\n[Error loading clarifications]' } : t));
          } finally {
            setTypingTurnId(null);
            userScrolledAwayRef.current = false;
          }
        })();

        const optimizationsPromise = (async () => {
          try {
            setIsLoadingSuggestions(true);
            setTypingSuggestionId(clientTurnId);
            let streamed = '';
            let firstChunk = false;

            await streamOptimizations(updatedValidMessages, fileMsgs, (chunk) => {
              if (!firstChunk) { setIsLoadingSuggestions(false); firstChunk = true; }
              streamed += chunk;
              optimizationText = streamed;
              setSuggestions(prev => ({ ...prev, [clientTurnId]: streamed }));
            });

            // Extract suggestions after streaming completes
            try {
              const extractResult = await extractOptimizations(optimizationText);
              if (extractResult.status === 'success' && extractResult.message) {
                extractedOptimizationsData = extractResult.message;
                setExtractedSuggestions(prev => ({ ...prev, [clientTurnId]: extractResult.message }));
              } else {
                extractedOptimizationsData = {};
                setExtractedSuggestions(prev => ({ ...prev, [clientTurnId]: {} }));
              }
            } catch (err) {
              console.error('Extract error:', err);
              extractedOptimizationsData = {};
              setExtractedSuggestions(prev => ({ ...prev, [clientTurnId]: {} }));
            }
          } catch (err) {
            console.error('Optimizations error:', err);
            setSuggestions(prev => ({ ...prev, [clientTurnId]: (prev[clientTurnId] || '') + '\n\n[Error loading suggestions]' }));
          } finally {
            setTypingSuggestionId(null);
            userScrolledAwayRightRef.current = false;
            setIsLoadingSuggestions(false);
          }
        })();

        await Promise.all([clarificationsPromise, optimizationsPromise]);

        // ── Step E: Save full turn to DB after streaming completes ──
        if (currentProjectId && dbTurnId) {
          try {
            const res = await fetchWithAuth(
              `${API}/api/projects/${currentProjectId}/turns/${dbTurnId}`,
              {
                method: 'PATCH',                              // ← was POST
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  assistantMessage: assistantMessageContent,
                  suggestionSummary: optimizationText,
                  suggestions: extractedOptimizationsData,
                  status: 'completed',                        // ← new
                }),
              }
            );
            const saved = await res.json();

            if (saved.suggestionOptions?.length > 0) {
              const optMap = {};
              saved.suggestionOptions.forEach(opt => { optMap[opt.title] = opt.id; });
              suggestionOptionMappingsRef.current[clientTurnId] = optMap;
            }
          } catch (err) {
            console.error('Failed to save turn:', err);
          }
        }

        // if (files?.length > 0) {
        //   setAllFiles(prev => [...prev, ...files]);
        // }

      } else {
        const errorMessage = "I apologize, but your query doesn't appear to be related to construction, structural engineering, or optimization. Please ask a question within these domains.";

        // ── Guardrail failed ── save turn as rejected for analysis ──
        if (currentProjectId && dbTurnId) {
          fetchWithAuth(`${API}/api/projects/${currentProjectId}/turns/${dbTurnId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'rejected', assistantMessage: errorMessage }),
          }).catch(() => { }); // fire-and-forget
        }

        let streamed = '';
        await streamText(errorMessage, (ch) => {
          streamed += ch;
          setTurns(prev => prev.map(t => t.id === clientTurnId ? { ...t, botFollowup: streamed } : t));
        });
        setTypingTurnId(null);
        userScrolledAwayRef.current = false;
      }

    } catch (err) {
      setIsLoadingSuggestions(false);
      console.error('handleSend error:', err);
      const errorMessage = 'Sorry, I encountered an error processing your request. Please try again.';
      let streamed = '';
      await streamText(errorMessage, (ch) => {
        streamed += ch;
        setTurns(prev => prev.map(t => t.id === clientTurnId ? { ...t, botFollowup: streamed } : t));
      });
      setTypingTurnId(null);
      userScrolledAwayRef.current = false;
    } finally {
      setIsSending(false);
    }
  };

  // ─── Report generation ─────────────────────────────────────────
  const handleGenerateReport = async (turnId, overrideSelections = null) => {
    const checkedSuggestions = overrideSelections !== null
      ? overrideSelections
      : (selectedSuggestions[turnId] || []);

    const optimizationText = suggestions[turnId] || '';
    const existingReports = reports[turnId] || [];
    const newVersion = existingReports.length + 1;
    const reportStartTime = Date.now();

    setActiveReportId({ turnId, version: newVersion });
    userScrolledAwayRightRef.current = false;

    setReports(prev => ({
      ...prev,
      [turnId]: [...(prev[turnId] || []), {
        version: newVersion,
        content: '',
        measurements: '',
        isComplete: false,
        selectedSuggestions: checkedSuggestions,
      }],
    }));

    try {
      setTypingReportId({ turnId, version: newVersion });
      setIsLoadingReport(true);
      setReportLoadingPhase('measurements');

      const fileMsgs = allFiles.length > 0 ? await convertFilesToBase64(allFiles) : [];

      const measurementsResult = await getMeasurements(validUserMessages, fileMsgs);
      const measurementsText = measurementsResult.message || '';

      setReports(prev => ({
        ...prev,
        [turnId]: prev[turnId].map(r => r.version === newVersion ? { ...r, measurements: measurementsText } : r),
      }));

      setReportLoadingPhase('costs');
      const costDetailsResult = await getMaterialCostList(validUserMessages, optimizationText, checkedSuggestions, measurementsText);
      const costDetailsText = costDetailsResult.message || '';

      setReportLoadingPhase('streaming');
      let streamedReport = '';
      let firstChunk = false;

      await streamReport(validUserMessages, optimizationText, checkedSuggestions, measurementsText, costDetailsText, (chunk) => {
        if (!firstChunk) { setIsLoadingReport(false); firstChunk = true; }
        streamedReport += chunk;
        setReports(prev => ({
          ...prev,
          [turnId]: prev[turnId].map(r => r.version === newVersion ? { ...r, content: streamedReport } : r),
        }));
      });

      // ── Save report to DB after streaming completes ──
      const dbTurnId = turnMappingsRef.current[turnId];
      if (dbTurnId) {
        try {
          const optMap = suggestionOptionMappingsRef.current[turnId] || {};
          const selectedOptionIds = checkedSuggestions
            .map(title => optMap[title])
            .filter(Boolean);

          await fetchWithAuth(`${API}/api/projects/${projectId}/turns/${dbTurnId}/reports`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              version: newVersion,
              markdown: streamedReport,
              selectedOptionIds,
              generationDurationMs: Date.now() - reportStartTime,
            }),
          });
        } catch (err) {
          console.error('Failed to save report:', err);
        }
      }

      // Cache PDF
      // ── Cache PDF locally + upload to S3 ──
      try {
        const pdfRes = await fetchWithAuth(`${API}/pdf/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ markdown: streamedReport, title: 'Alpherra Report', version: newVersion }),
        });

        if (pdfRes.ok) {
          const pdfBlob = await pdfRes.blob();

          // ✅ Keep local IndexedDB cache (for fast download in current session)
          await savePDFToCache(turnId, newVersion, pdfBlob);

          // ✅ Upload PDF to S3 as generated_report TurnFile
          // const dbTurnId = turnMappingsRef.current[turnId];
          // const turn = turns.find(t => t.id === turnId);

          // if (dbTurnId && projectId && turn) {
          //   try {
          //     const pdfFile = new File(
          //       [pdfBlob],
          //       `report_v${newVersion}.pdf`,
          //       { type: 'application/pdf' }
          //     );

          //     const formData = new FormData();
          //     formData.append('file', pdfFile);
          //     formData.append('projectId', projectId);
          //     formData.append('turnNumber', String(turn.turnNumber));
          //     formData.append('originalFilename', `report_v${newVersion}.pdf`);
          //     formData.append('fileType', 'generated_report'); // ← tells files.js which type to save

          //     await fetchWithAuth(`${API}/files/upload`, {
          //       method: 'POST',
          //       body: formData,
          //     });

          //     console.log(`✅ Report PDF v${newVersion} uploaded to S3`);
          //   } catch (uploadErr) {
          //     console.warn('PDF S3 upload failed (non-fatal):', uploadErr);
          //     // Don't throw — PDF is still cached locally, user can still download
          //   }
          // }
        }
      } catch (err) {
        console.warn('PDF cache/upload failed:', err);
      }

      // try {
      //   const pdfRes = await fetchWithAuth(`${API}/pdf/generate`, {
      //     method: 'POST',
      //     headers: { 'Content-Type': 'application/json' },
      //     body: JSON.stringify({ markdown: streamedReport, title: 'Alpherra Report', version: newVersion }),
      //   });
      //   if (pdfRes.ok) {
      //     const pdfBlob = await pdfRes.blob();
      //     await savePDFToCache(turnId, newVersion, pdfBlob);
      //   }
      // } catch (err) {
      //   console.warn('PDF cache failed:', err);
      // }

      setReports(prev => ({
        ...prev,
        [turnId]: prev[turnId].map(r => r.version === newVersion ? { ...r, isComplete: true } : r),
      }));

    } catch (err) {
      console.error('Report generation error:', err);
      setReports(prev => ({
        ...prev,
        [turnId]: prev[turnId].map(r =>
          r.version === newVersion ? { ...r, content: r.content + '\n\n[Error generating report]', isComplete: true } : r
        ),
      }));
    } finally {
      setTypingReportId(null);
      userScrolledAwayRightRef.current = false;
      setIsLoadingReport(false);
      setReportLoadingPhase('init');
    }
  };

  // ─── Feedback handlers ─────────────────────────────────────────
  const saveFeedbackManually = async (turnId, feedbackType, comment = '') => {
    const dbTurnId = turnMappingsRef.current[turnId];
    if (!dbTurnId || !projectId) return;

    try {
      await fetchWithAuth(`${API}/api/projects/${projectId}/turns/${dbTurnId}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: feedbackType, comment }),
      });
    } catch (err) {
      console.error('Feedback save error:', err);
    }
  };

  const handleRightPaneFeedback = (contentId, feedbackType, comment, metadata) => {
    if (metadata.contentType === 'suggestions') {
      saveSuggestionsFeedbackManually(metadata.turnId, feedbackType, comment);
    } else if (metadata.contentType === 'report') {
      saveReportFeedbackManually(metadata.turnId, metadata.version, feedbackType, comment);
    }
  };

  // ── Suggestions feedback (Option A — whole turn) ──────────────
  const saveSuggestionsFeedbackManually = async (turnId, feedbackType, comment = '') => {
    const dbTurnId = turnMappingsRef.current[turnId];
    if (!dbTurnId || !projectId) return;

    try {
      await fetchWithAuth(`${API}/api/projects/${projectId}/turns/${dbTurnId}/suggestions/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: feedbackType, comment }),
      });
    } catch (err) {
      console.error('Suggestions feedback error:', err);
    }
  };

  const saveReportFeedbackManually = async (turnId, reportVersion, feedbackType, comment = '') => {
    const dbTurnId = turnMappingsRef.current[turnId];
    if (!dbTurnId || !projectId) return;

    try {
      // Fetch report DB id first
      const res = await fetchWithAuth(
        `${API}/api/projects/${projectId}/turns/${dbTurnId}/reports?version=${reportVersion}`,
      );
      const data = await res.json();
      if (!data.reportId) return;

      await fetchWithAuth(`${API}/api/projects/${projectId}/turns/${dbTurnId}/reports/${data.reportId}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: feedbackType, comment }),
      });
    } catch (err) {
      console.error('Report feedback error:', err);
    }
  };

  // ─── Toggle handlers ───────────────────────────────────────────
  const toggleSuggestion = (id) => {
    if (activeSuggestionTurnId === id && !activeReportId) {
      setActiveSuggestionTurnId(null);
    } else {
      setActiveSuggestionTurnId(id);
      setActiveReportId(null);
    }
  };

  const toggleReport = (turnId, version) => {
    if (activeReportId?.turnId === turnId && activeReportId?.version === version) {
      setActiveReportId(null);
      setActiveSuggestionTurnId(null);
    } else {
      setActiveReportId({ turnId, version });
      setActiveSuggestionTurnId(turnId);
    }
  };

  const confirmSuggestionSelection = (turnId, checkedKeys) => {
    setSelectedSuggestions(prev => ({ ...prev, [turnId]: checkedKeys }));
  };

  // ─── Computed values ───────────────────────────────────────────
  const activeTurn = activeSuggestionTurnId
    ? turns.find(t => t.id === activeSuggestionTurnId) || null
    : null;
  const isRightPaneOpen = !!activeTurn;

  const getRightPaneContent = () => {
    if (!activeSuggestionTurnId) return null;
    if (activeReportId && activeReportId.turnId === activeSuggestionTurnId) {
      const report = (reports[activeReportId.turnId] || []).find(r => r.version === activeReportId.version);
      return {
        type: 'report',
        content: report?.content || '',
        isComplete: report?.isComplete || false,
        version: activeReportId.version,
        selectedSuggestions: report?.selectedSuggestions || [],
      };
    }
    return {
      type: 'suggestions',
      content: suggestions[activeSuggestionTurnId] || '',
      extracted: extractedSuggestions[activeSuggestionTurnId] || {},
      selected: selectedSuggestions[activeSuggestionTurnId] || [],
    };
  };

  // ─── Exports ───────────────────────────────────────────────────
  return {
    chatStarted,
    isSending,
    turns,
    activeSuggestionTurnId,
    suggestions,
    typingTurnId,
    typingSuggestionId,
    activeTurn,
    isRightPaneOpen,
    extractedSuggestions,
    selectedSuggestions,
    reports,
    activeReportId,
    typingReportId,
    isLoadingSuggestions,
    isLoadingReport,
    reportLoadingPhase,
    setReportLoadingPhase,
    messagesContainerRef,
    rightPaneRef,

    // Project
    projectId,
    projectName,

    // Pagination
    hasMoreTurns,
    isLoadingOlderTurns,

    //feedback
    feedbackState, setFeedbackState,
    suggestionFeedbackState, setSuggestionFeedbackState,
    reportFeedbackState, setReportFeedbackState,

    // Handlers
    handleSend,
    toggleSuggestion,
    toggleReport,
    onScrollMessages,
    handleRightPaneScroll,
    closeSuggestionPane: () => { setActiveSuggestionTurnId(null); setActiveReportId(null); },
    confirmSuggestionSelection,
    handleGenerateReport,
    getRightPaneContent,
    handleRightPaneFeedback,
    saveFeedbackManually,
    saveSuggestionsFeedbackManually,
    saveReportFeedbackManually,


    resetChat,
    loadProject,
    loadOlderTurns,
    renameProject,
  };
}
